import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type {
  RunReportFilterIndex,
  RunReportPortfolioFilterRow,
  RunReportPortfolioFilters,
} from '../../types/runTable.types';

export type {
  RunReportAssetFilterOption,
  RunReportFilterIndex,
  RunReportModelFilterOption,
  RunReportPortfolioFilterRow,
  RunReportPortfolioFilters,
} from '../../types/runTable.types';

export const hasActivePortfolioFilters = (
  filters: RunReportPortfolioFilters,
): boolean =>
  Boolean(
    filters.searchQuery?.trim() ||
      filters.modelPortfolioId ||
      filters.financialProductId,
  );

export const resolveRowModelPortfolioId = (
  row: RunReportPortfolioFilterRow,
  reportsById?: Map<string, PortfolioReport>,
): string | undefined =>
  row.modelPortfolioId ?? reportsById?.get(row.reportId)?.modelPortfolio?.id;

export const countRowsMatchingModel = (
  rows: RunReportPortfolioFilterRow[],
  modelPortfolioId: string,
  reportsById?: Map<string, PortfolioReport>,
): number =>
  rows.filter(
    (row) => resolveRowModelPortfolioId(row, reportsById) === modelPortfolioId,
  ).length;

export const getReportIdsTradingAsset = (
  audits: OptimizerAuditV2Record[],
  financialProductId: string,
): Set<string> => {
  const reportIds = new Set<string>();

  for (const audit of audits) {
    if (!audit.portfolioReportId) {
      continue;
    }

    const hasAssetTrade = (audit.subAccountResults ?? []).some((subAccount) =>
      (subAccount.weightDiffs ?? []).some(
        (diff) =>
          diff.financialProductId === financialProductId &&
          diff.deltaCents != null &&
          diff.deltaCents !== 0,
      ),
    );

    if (hasAssetTrade) {
      reportIds.add(audit.portfolioReportId);
    }
  }

  return reportIds;
};

export const countReportIdsTradingAsset = (
  audits: OptimizerAuditV2Record[],
  financialProductId: string,
): number => getReportIdsTradingAsset(audits, financialProductId).size;

const formatAssetTicker = (
  financialProductId: string,
  tickerMap: Record<string, string>,
): string =>
  tickerMap[financialProductId] ?? financialProductId.slice(-8).toUpperCase();

export const buildRunReportFilterIndex = (
  translateBackendString: (translatedObject: unknown) => string,
  rows: RunReportPortfolioFilterRow[],
  audits: OptimizerAuditV2Record[],
  tickerMap: Record<string, string>,
  reportsById?: Map<string, PortfolioReport>,
  activeFilters: RunReportPortfolioFilters = {},
): RunReportFilterIndex => {
  const assetScopedReportIds = activeFilters.financialProductId
    ? getReportIdsTradingAsset(audits, activeFilters.financialProductId)
    : undefined;
  const rowsForModelCounts = assetScopedReportIds
    ? rows.filter((row) => assetScopedReportIds.has(row.reportId))
    : rows;
  const rowsForAssetCounts = activeFilters.modelPortfolioId
    ? rows.filter(
        (row) =>
          resolveRowModelPortfolioId(row, reportsById) ===
          activeFilters.modelPortfolioId,
      )
    : rows;
  const assetScopeReportIds = new Set(
    rowsForAssetCounts.map((row) => row.reportId),
  );

  const modelCounts = new Map<string, { label: string; count: number }>();

  for (const row of rowsForModelCounts) {
    const modelId = resolveRowModelPortfolioId(row, reportsById);

    if (!modelId) {
      continue;
    }

    const report = reportsById?.get(row.reportId);
    const label =
      row.modelLabel ||
      (report
        ? translateBackendString(report.modelPortfolio?.translatedName)
        : '') ||
      modelId;
    const existing = modelCounts.get(modelId);

    if (existing) {
      existing.count += 1;
    } else {
      modelCounts.set(modelId, { label, count: 1 });
    }
  }

  const assetReportIds = new Map<string, Set<string>>();

  for (const audit of audits) {
    if (
      !audit.portfolioReportId ||
      !assetScopeReportIds.has(audit.portfolioReportId)
    ) {
      continue;
    }

    const assetsInAudit = new Set<string>();

    for (const subAccount of audit.subAccountResults ?? []) {
      for (const diff of subAccount.weightDiffs ?? []) {
        if (
          !diff.financialProductId ||
          diff.deltaCents == null ||
          diff.deltaCents === 0
        ) {
          continue;
        }

        assetsInAudit.add(diff.financialProductId);
      }
    }

    for (const financialProductId of assetsInAudit) {
      const reportIds =
        assetReportIds.get(financialProductId) ?? new Set<string>();
      reportIds.add(audit.portfolioReportId);
      assetReportIds.set(financialProductId, reportIds);
    }
  }

  const modelOptions = [...modelCounts.entries()]
    .map(([id, value]) => ({
      id,
      label: value.label,
      count: value.count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  const assetOptions = [...assetReportIds.entries()]
    .map(([financialProductId, reportIds]) => ({
      financialProductId,
      ticker: formatAssetTicker(financialProductId, tickerMap),
      count: reportIds.size,
    }))
    .sort((a, b) => b.count - a.count || a.ticker.localeCompare(b.ticker));

  return {
    modelOptions,
    assetOptions,
  };
};

export const matchesPortfolioFilters = (
  row: RunReportPortfolioFilterRow,
  filters: RunReportPortfolioFilters,
  audits: OptimizerAuditV2Record[],
  reportsById?: Map<string, PortfolioReport>,
): boolean => {
  const searchQuery = filters.searchQuery?.trim().toLowerCase();
  const modelPortfolioId = resolveRowModelPortfolioId(row, reportsById);

  if (searchQuery) {
    const haystack = [row.clientLabel, row.goalLabel, row.modelLabel]
      .join(' ')
      .toLowerCase();

    if (!haystack.includes(searchQuery)) {
      return false;
    }
  }

  if (
    filters.modelPortfolioId &&
    modelPortfolioId !== filters.modelPortfolioId
  ) {
    return false;
  }

  if (filters.financialProductId) {
    const matchingReportIds = getReportIdsTradingAsset(
      audits,
      filters.financialProductId,
    );

    if (!matchingReportIds.has(row.reportId)) {
      return false;
    }
  }

  return true;
};
